import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    CircularProgress,
    Alert,
    Snackbar,
} from '@mui/material';
import MuiAlert, { AlertProps } from '@mui/material/Alert';
import { getStartSurvey, submitStartSurvey } from '../../services/questionnaires/questionnaire.service';
import { Questionnaire } from '../../types/questionnaire';
import { QuestionnaireRenderer } from '../../services/questionnaires/questionnaireRenderer';
import { ModalIndices } from '../../common/i-doc/i-doc.registry.ts';

const AlertSnackbar = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
    props,
    ref,
) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

interface InitialSurveyModalProps {
    open: boolean;
    onClose: () => void;
    onSurveyCompleted?: () => void; // Додано новий пропс
}

export const InitialSurveyModal: React.FC<InitialSurveyModalProps> = ({ open, onClose, onSurveyCompleted }) => {
    const [questionnaire, setQuestionnaire] = useState<Questionnaire | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [answers, setAnswers] = useState<Record<string, any>>({});
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

    useEffect(() => {
        if (open) {
            const fetchSurvey = async () => {
                try {
                    setLoading(true);
                    setError(null);
                    const data = await getStartSurvey();
                    if (data) {
                        setQuestionnaire(data);
                        setAnswers(data.answers || {});
                    } else {
                        setError('Не вдалося завантажити опитувальник.');
                    }
                } catch (err) {
                    setError('Помилка при завантаженні даних.');
                    console.error(err);
                } finally {
                    setLoading(false);
                }
            };
            fetchSurvey();
        }
    }, [open]);

    const handleAnswerChange = (questionName: string, value: any) => {
        setAnswers((prevAnswers) => ({
            ...prevAnswers,
            [questionName]: value,
        }));
    };

    const handleSave = async () => {
        if (!questionnaire) return;
        try {
            setLoading(true);
            // Використовуємо спеціальну функцію для відправки стартового опитування,
            // яка завжди відправляє POST-запит на .../questionnaires/start-survey,
            // ігноруючи числовий ID самого опитувальника.
            const response = await submitStartSurvey({ answers });
            if (response.validation.isValid) {
                setSnackbarMessage('Відповіді успішно збережено!');
                setSnackbarSeverity('success');
                setSnackbarOpen(true);
                onClose(); // Close modal on successful save
                onSurveyCompleted?.(); // Викликаємо новий пропс після успішного збереження
            } else {
                const errorMessages = response.validation.errors.map(err => err.message).join(', ');
                setSnackbarMessage(`Помилки валідації: ${errorMessages}`);
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
            }
        } catch (err) {
            setSnackbarMessage('Помилка при збереженні відповідей.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleCloseSnackbar = (_event?: React.SyntheticEvent | Event, reason?: string) => { // Змінено event на _event
        if (reason === 'clickaway') {
            return;
        }
        setSnackbarOpen(false);
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" data-i-doc={ModalIndices.INITIAL_SURVEY}>
                <DialogTitle>{questionnaire ? questionnaire.name : 'Завантаження...'}</DialogTitle>
                <DialogContent dividers>
                    {loading && (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                            <CircularProgress />
                        </Box>
                    )}
                    {error && <Alert severity="error">{error}</Alert>}
                    {questionnaire && !loading && !error && (
                        <>
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body1" color="text.secondary">
                                    {questionnaire.description}
                                </Typography>
                                {questionnaire.welcomeNotes && (
                                    <Alert severity="info" sx={{ mt: 2 }}>
                                        {questionnaire.welcomeNotes}
                                    </Alert>
                                )}
                            </Box>
                            <QuestionnaireRenderer
                                questionnaire={questionnaire}
                                answers={answers}
                                onAnswerChange={handleAnswerChange}
                            />
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Пропустити</Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={loading || !questionnaire}
                    >
                        {loading ? <CircularProgress size={24} /> : 'Зберегти та продовжити'}
                    </Button>
                </DialogActions>
            </Dialog>
            <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleCloseSnackbar}>
                <AlertSnackbar onClose={handleCloseSnackbar} severity={snackbarSeverity}>
                    {snackbarMessage}
                </AlertSnackbar>
            </Snackbar>
        </>
    );
};
